import api from "@/shared/api/api";
import { Method } from "axios";
import { ActionKeyPair } from '@/shared/hooks/useCustomQuery';
import { PaginatedResponse } from "@/features/profiles/services/profiles/types";

export enum LanguageCode {
    PT_BR = 'PT_BR',
    EN = 'EN',
    ES = 'ES',
}

export interface TagTranslation {
    languageCode: LanguageCode;
    name: string;
}

export interface Tag {
    id: string;
    published: boolean;
    isCategory: boolean;
    translations: TagTranslation[];
    createdAt: string;
    updatedAt: string;
}

export interface TagUpdateData {
    published: boolean;
    isCategory: boolean;
    translations: TagTranslation[];
    replaceTranslations: boolean;
}

export interface CreateTagData {
    published: boolean;
    isCategory: boolean;
    translations: TagTranslation[];
}

export class TagsService {
    [key: string]: ActionKeyPair<unknown, unknown>;

    // List all tags with pagination and search
    getTags: ActionKeyPair<
        { search?: string; page?: number, limit?: number },
        PaginatedResponse<Tag>
    > = {
            key: "get-tags",
            actionBuilder: (params) => async () => {
                return await api<PaginatedResponse<Tag>>({
                    url: "/v1/tags",
                    method: "GET" as Method,
                    params,
                })
            },
        };

    // Get a single tag by ID
    getTag: ActionKeyPair<string, Tag> = {
        key: "get-tag",
        actionBuilder: (id) => async () => {
            return await api<Tag>({
                url: `/v1/tags/${id}`,
                method: "GET" as Method,
            });
        },
    };

    // Create a new tag
    create: ActionKeyPair<CreateTagData, Tag> = {
        key: "create-tag",
        actionBuilder: (data) => async () => {
            const response = await api<Tag>({
                url: "/v1/tags",
                method: "POST" as Method,
                data,
            });
            return response;
        },
    };

    // Update an existing tag
    update: ActionKeyPair<{ id: string; data: TagUpdateData }, Tag> = {
        key: "update-tag",
        actionBuilder: ({ id, data }) => async () => {
            const response = await api<Tag>({
                url: `/v1/tags/${id}`,
                method: "PATCH" as Method,
                data,
            });
            return response;
        },
    };

    // Delete a tag
    delete: ActionKeyPair<string, void> = {
        key: "delete-tag",
        actionBuilder: (id) => async () => {
            await api({
                url: `/v1/tags/${id}`,
                method: "DELETE" as Method,
            });
        },
    };

    // Toggle tag published status
    togglePublish: ActionKeyPair<{ id: string; published: boolean }, Tag> = {
        key: "toggle-publish-tag",
        actionBuilder: ({ id, published }) => async () => {
            const response = await api<Tag>({
                url: `/v1/tags/${id}/publish`,
                method: "PATCH" as Method,
                data: { published },
            });
            return response;
        },
    };

    // Link a tag to a pack
    linkTagToPack: ActionKeyPair<{ tagId: string; packId: string }, void> = {
        key: "link-tag-to-pack",
        actionBuilder: ({ tagId, packId }) => async () => {
            await api({
                url: `/v1/tags/${tagId}/packs`,
                method: "POST" as Method,
                data: { id: packId },
            });
        },
    };

    // Unlink a tag from a pack
    unlinkTagFromPack: ActionKeyPair<{ tagId: string; packId: string }, void> = {
        key: "unlink-tag-from-pack",
        actionBuilder: ({ tagId, packId }) => async () => {
            await api({
                url: `/v1/tags/${tagId}/packs/${packId}`,
                method: "DELETE" as Method,
            });
        },
    };

    // Link a tag to a preset
    linkTagToPreset: ActionKeyPair<{ tagId: string; presetId: string }, void> = {
        key: "link-tag-to-preset",
        actionBuilder: ({ tagId, presetId }) => async () => {
            await api({
                url: `/v1/tags/${tagId}/presets`,
                method: "POST" as Method,
                data: { id: presetId },
            });
        },
    };

    // Unlink a tag from a preset
    unlinkTagFromPreset: ActionKeyPair<{ tagId: string; presetId: string }, void> = {
        key: "unlink-tag-from-preset",
        actionBuilder: ({ tagId, presetId }) => async () => {
            await api({
                url: `/v1/tags/${tagId}/presets/${presetId}`,
                method: "DELETE" as Method,
            });
        },
    };

    static Instance = new TagsService();
}

export default TagsService;
